import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import api from '../utils/api';
import InviteLinksModal from './InviteLinksModal';
import {
  LayoutDashboard, Users, Trophy, BarChart3, LogOut, Menu, X, ChevronRight, Shield, Smartphone, ArrowLeftRight, Bell, Settings, Link2, HelpCircle, Mail
} from 'lucide-react';

const navItems = (clubRole, userRole) => {
  const items = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/tournaments', label: 'Tournaments', icon: Trophy },
    { to: '/standings', label: 'Standings', icon: BarChart3 },
  ];
  if (clubRole === 'admin' || clubRole === 'contributor') {
    items.splice(1, 0, { to: '/players', label: 'Players', icon: Users });
  }
  if (userRole === 'admin') {
    items.push({ to: '/admin', label: 'Admin', icon: Shield });
  }
  items.push({ to: '/settings', label: 'Settings', icon: Settings });
  items.push({ to: '/invites', label: 'Invites', icon: Mail });
  items.push({ to: '/help', label: 'Help', icon: HelpCircle });
  items.push({ to: '/mobile-preview', label: 'Mobile App', icon: Smartphone });
  return items;
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { selectedClub, clearClub } = useClub();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const isClubAdmin = selectedClub?.my_role === 'admin';

  // Fetch unread notification count
  useEffect(() => {
    const fetchCount = () => {
      api.get('/notifications/unread-count')
        .then(res => setUnreadCount(res.data.count))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?limit=10');
      setNotifications(res.data);
    } catch {}
  };

  const handleBellClick = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const markAsRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setNotifications(n => n.map(x => x.id === id ? {...x, read: true} : x));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const handleLogout = () => {
    clearClub();
    logout();
    navigate('/login');
  };

  const handleSwitchClub = () => {
    clearClub();
    navigate('/clubs');
  };

  const roleColor = {
    admin: 'bg-green-100 text-green-700',
    contributor: 'bg-blue-100 text-blue-700',
    player: 'bg-slate-100 text-slate-600',
  };

  const Sidebar = () => (
    <aside className="flex flex-col bg-white border-r border-slate-200 w-64" style={{ height: '100dvh', maxHeight: '100dvh' }}>
      {/* Logo — pinned top */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 flex-shrink-0">
        <div className="w-9 h-9 bg-green-600 rounded-lg flex items-center justify-center">
          <Trophy className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-bold text-slate-900 text-sm leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Jump Smash</p>
          <p className="text-xs text-slate-400">Game Manager</p>
        </div>
      </div>

      {/* Current Club — pinned */}
      <div className="px-3 py-2.5 border-b border-slate-100 flex-shrink-0">
        <div 
          onClick={handleSwitchClub}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
        >
          <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <Users size={14} className="text-green-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{selectedClub?.name}</p>
            <p className="text-[10px] text-slate-500 capitalize">{selectedClub?.my_role}</p>
          </div>
          <ArrowLeftRight size={14} className="text-slate-400" />
        </div>
        {isClubAdmin && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-all border border-green-200"
            data-testid="invite-members-btn"
          >
            <Link2 size={12} /> Invite Members
          </button>
        )}
      </div>

      {/* Nav — scrollable middle */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" style={{ WebkitOverflowScrolling: 'touch' }}>
        {navItems(selectedClub?.my_role, user?.role).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 group ${
                isActive
                  ? 'bg-green-50 text-green-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`
            }
            data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-4.5 h-4.5 ${isActive ? 'text-green-600' : 'text-slate-400 group-hover:text-slate-600'}`} size={18} />
                <span>{label}</span>
                {isActive && <ChevronRight className="ml-auto w-3.5 h-3.5 text-green-500" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer — pinned bottom */}
      <div className="px-3 pb-3 pt-2 border-t border-slate-100 flex-shrink-0">
        {/* Notifications */}
        <div className="relative mb-1">
          <button
            onClick={handleBellClick}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-all"
            data-testid="notifications-bell"
          >
            <div className="relative">
              <Bell size={18} className="text-slate-500" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <span className="text-sm text-slate-600">Notifications</span>
          </button>
          
          {/* Notifications dropdown */}
          {showNotifications && (
            <div className="absolute bottom-full left-0 mb-2 w-72 bg-white rounded-xl border border-slate-200 shadow-lg z-50 max-h-80 overflow-y-auto">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-bold text-slate-900">Notifications</span>
                <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">No notifications</div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n.id}
                    onClick={() => { markAsRead(n.id); if (n.link) navigate(n.link); setShowNotifications(false); }}
                    className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${!n.read ? 'bg-green-50' : ''}`}
                  >
                    <p className="text-sm font-medium text-slate-900">{n.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-xs">
            {user?.username?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.username}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${roleColor[selectedClub?.my_role] || 'bg-slate-100 text-slate-600'}`}>
              {selectedClub?.my_role || user?.role}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50"
            data-testid="logout-button"
            title="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex bg-slate-50 overflow-hidden" style={{ height: '100dvh' }}>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex flex-col w-64" style={{ height: '100dvh' }}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-slate-600 hover:text-slate-900 p-1"
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-green-600 rounded-md flex items-center justify-center">
              <Trophy className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Jump Smash</span>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-2 sm:p-3" style={{ WebkitOverflowScrolling: 'touch' }}>
          <Outlet />
        </main>
      </div>

      {showInviteModal && <InviteLinksModal onClose={() => setShowInviteModal(false)} />}
    </div>
  );
}
